import * as R from 'rambda'
import log from 'loglevel'
import { ModelRecord } from '@trustero/trustero-api-web/lib/model/model_pb'
import React from 'react'
import { Message } from 'google-protobuf'
import { MODEL_TYPE } from '@trustero/trustero-api-web/lib/common/model_pb'
import * as logos from '../components/Icons/Dependencies'
import { Content, Model } from '../context/Content/defs'
import { ContentStaticHelper } from '../context/Content/statichelpers'
import { AuthAction, AuthRecord } from '../context/authContext'
import { Service } from '../xgenerated'
import { ServiceCategory, ServiceType } from '../xgenerated/service'
import { GrpcCall } from '../components/async/utils'
import DataModelAdapter from './DataModelAdapter'

const logger = log.getLogger('adapter')

// THIS IS THE CORE FUNCTION IN THIS FILE
// 1. make the API call
// 2. read the activity feed to invalidate caches for what changed
// 3. pass the updated sync watermark to the `ContentContext`
export async function callApiAndUpdateContent<T>(
  api_func: () => Promise<T | undefined>,
  content: Content,
  contentDispatch: React.Dispatch<Content>,
  // A getter is read after the write, so the content refresh uses a token
  // refreshed while the write was in flight.
  authCtx: AuthRecord | (() => AuthRecord),
  authDispatch: React.Dispatch<AuthAction>,
  methodMutator: (asyncCall: unknown) => Promise<unknown>,
  methodRequestMutator: (
    asyncCall: GrpcCall<Message, Message>,
    request: Message,
  ) => Promise<Message>,
): Promise<T | undefined> {
  const result = await api_func()
  const delta = await DataModelAdapter.getDeltaUpdate(
    content.lastUpdateStamp + 1,
    content,
    typeof authCtx === 'function' ? authCtx() : authCtx,
    authDispatch,
    methodMutator,
    methodRequestMutator,
  )
  if (delta && Object.keys(delta).length > 0) {
    contentDispatch(delta)
  }
  return result
}

/**
 * Clones an existing local model object or creates a new one if necessary.
 */
export function createLocalModel(
  content: Content,
  modelType: MODEL_TYPE,
  modelId: string,
): Model | undefined {
  const doCreateLocalModel = () => {
    if (modelType === MODEL_TYPE.USEROBJ) {
      const userModel = ContentStaticHelper.getObjById(content, modelId)
      return userModel ? R.clone(userModel) : null
    }
    if (modelType === MODEL_TYPE.SERVICE && !content.idIndex[modelId]) {
      return {
        ...({} as ModelRecord.AsObject),
        id: modelId,
        name: 'Unknown Service',
        content: 'This service is specific to your organization.',
        custom: true,
        component_category: ServiceCategory['Business Services and Tools'],
        component_type: ServiceType['SaaS'],
        control_ids: [],
        recommended_service: false,
        detected: true,
        logo: logos.UnknownSaaS,
        logo_name: 'UnknownSaaS',
      } as Service
    }
    const modelObj = content.idIndex[modelId]
    return modelObj ? R.clone(modelObj) : null
  }

  const localModel = doCreateLocalModel()
  if (localModel) {
    return localModel
  }

  logger.warn(`Unsupported model type: ${modelType}  , ${modelId}`)
  return
}
